import React, {useEffect, useMemo, useState} from 'react';
import {Button, Modal, ModalBody, ModalFooter} from "react-bootstrap";
import ModalHeader from "react-bootstrap/ModalHeader";

import "./SearchModal.scss"
import classes from "./SearchModal.scss.json"

import AgencyPicker from "./AgencyPicker";
import DatePicker from "./DatePicker";
import TimePicker from "./TimePicker";

import cx from "classnames"

import {
    addDays,
    addHours,
    differenceInDays,
    differenceInHours,
    format,
    getDay,
    isAfter,
    isBefore,
    isValid,
    parse,
    parseISO, setHours, getHours,
    startOfDay
} from "date-fns"
import sandbox from "../../../service/Sandbox";
import { getRentalDateErrors, mergeBookingParametersWithAgency } from "../../../service/bookingAgencyParams";
import { isHourBlockedByClosing, isMomentInClosingPeriod, parseClosingDate } from "../../utils/closingPeriods";
import frLocale from "date-fns/locale/fr"

const searchModalDisplayMode = {
    start_agency: 'start_agency',
    start_day: 'start_day',
    start_hour: 'start_hour',
    end_agency: 'end_agency',
    end_day: 'end_day',
    end_hour: 'end_hour',
    recap: 'recap',
}

const title = {
    start_agency: 'Agence de départ',
    start_day: 'Jour de début',
    start_hour: 'Heure de début',
    end_agency: 'Agence de Retour',
    end_day: 'Jour de retour',
    end_hour: 'Heure de retour',
}

const dateStepOrder = ['start_day', 'start_hour', 'end_day', 'end_hour']

function getFirstOpenDateAfterClosing(date, closingPeriods = []) {
    if (!isValid(date) || !closingPeriods?.length) {
        return date;
    }

    let nextDate = date;
    let changed = true;

    while (changed) {
        changed = false;
        closingPeriods.forEach((period) => {
            const endDate = parseClosingDate(period?.end_date);
            if (!endDate) return;

            if (isMomentInClosingPeriod(nextDate, [period], { inclusiveEnd: false })) {
                nextDate = endDate;
                changed = true;
            }
        });
    }

    return nextDate;
}

/** All weekdays (0=Sun … 6=Sat, same as date-fns getDay) where agency is fully closed. */
function getAllDayUnavailableWeekdays(availabilityPeriods) {
    const days = (availabilityPeriods ?? [])
        .filter((p) => p?.status === 'unavailable' && p?.all_day === true && typeof p?.day === 'number')
        .map((p) => p.day);
    return [...new Set(days)];
}

function getAgencyPeriodsForCalendar(agency, bookingParameters) {
    if (!agency && bookingParameters?.delivery_availability?.periods?.length > 0) {
        return bookingParameters.delivery_availability.periods;
    }
    return agency?.delivery_periods ?? agency?.availability_periods ?? [];
}

function normalizeAgencyNameForMatch(s) {
    return String(s ?? '')
        .trim()
        .toLowerCase()
        // best-effort accent/diacritics removal for matching "AÉROPORT" vs "Aeroport"
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Some environments return agency hours in two different places:
 * - `agencies[].availability_periods` (id like 8177)
 * - `bookingParameters.delivery_availability.periods` (id like 28576)
 *
 * When the latter is present, prefer it for calendar blocking and opening-hours display.
 * We match by name because IDs differ between the two datasets.
 */
function applyDeliveryAvailabilityOverride(agency, bookingParameters) {
    const override = bookingParameters?.delivery_availability;
    if (!agency || !override?.periods?.length) return agency;

    const agencyName = normalizeAgencyNameForMatch(agency?.public_name ?? agency?.name);
    const overrideName = normalizeAgencyNameForMatch(override?.name);
    if (!agencyName || !overrideName) return agency;

    // In some APIs the agency list can be generic ("Aéroport") while the booking parameters
    // carry the full label ("Aéroport Roland Garros"). Accept partial match.
    const namesMatch =
        agencyName === overrideName ||
        agencyName.includes(overrideName) ||
        overrideName.includes(agencyName);

    if (!namesMatch) return agency;

    return {
        ...agency,
        // used by our DatePicker + TimePicker logic with higher priority than availability_periods
        delivery_periods: override.periods,
    };
}

const SearchModal = ({selectedInput, toggleOpen, agencies, onChange, values, onSearch, color, bookingParameters, singleAgency = false, minDays, minStartDate, maxStartDate,maxTimeBeforeStart, disableAgencySelect = false, warningMessage, maxDays}) => {

    const [modalOpen, setModalOpen] = useState(false)
    const [displayMode, setDisplayMode] = useState(selectedInput);
    const [search, setSearch] = useState({
        start_agency: values?.start_agency ?? null,
        end_agency: values?.end_agency ?? null,
        start_day: '',
        end_day: '',
        start_hour : '',
        end_hour : '',
    })

    const [startAgencyHours, setStartAgencyHours] = useState([]);
    const [endAgencyHours, setEndAgencyHours] = useState([]);
    const [startUnavailableDays, setStartUnavailableDays] = useState([]);
    const [endUnavailableDays, setEndUnavailableDays] = useState([]);
    const [startHoursAndMinutes, setStartHoursAndMinutes] = useState([]);
    const [endHoursAndMinutes, setEndHoursAndMinutes] = useState([]);

    const startAgency = useMemo(() => {
        if (search.start_agency && agencies?.length) {
            const agency = agencies.find(item => item.id === Number(search.start_agency)) ?? null;
            return applyDeliveryAvailabilityOverride(agency, bookingParameters);
        }
        return null;
    }, [search.start_agency, agencies, bookingParameters]);

    const endAgency = useMemo(() => {
        if (search.end_agency && agencies?.length) {
            const agency = agencies.find(item => item.id === Number(search.end_agency)) ?? null;
            return applyDeliveryAvailabilityOverride(agency, bookingParameters);
        }
        return null;
    }, [search.end_agency, agencies, bookingParameters]);

    const agencyMinDays = useMemo(() => {
        return mergeBookingParametersWithAgency(startAgency, bookingParameters).bookingMinDays
    }, [startAgency, bookingParameters])

    const agencyMinHours = useMemo(() => {
        return mergeBookingParametersWithAgency(startAgency, bookingParameters).bookingMinHours
    }, [startAgency, bookingParameters])

    const agencyMaxDays = useMemo(() => {
        return mergeBookingParametersWithAgency(startAgency, bookingParameters).bookingMaxDays
    }, [startAgency, bookingParameters])

    const agencyMaxHours = useMemo(() => {
        return mergeBookingParametersWithAgency(startAgency, bookingParameters).bookingMaxHours
    }, [startAgency, bookingParameters])

    const dateStepDisplayModes = useMemo(() => ([
        searchModalDisplayMode.start_day,
        searchModalDisplayMode.start_hour,
        searchModalDisplayMode.end_day,
        searchModalDisplayMode.end_hour,
    ]), [])

    const getValidationErrorsForResult = (res, { requireDateStep = true } = {}) => {
        if (!isValid(res?.start_date) || !isValid(res?.end_date)) {
            return [];
        }
        if (requireDateStep && !dateStepDisplayModes.includes(displayMode)) {
            return [];
        }

        return getRentalDateErrors({
            startDate: res.start_date,
            endDate: res.end_date,
            minDays: agencyMinDays,
            minHours: agencyMinDays > 0 ? 0 : agencyMinHours,
            maxDays: agencyMaxDays,
            maxHours: agencyMaxDays > 0 ? 0 : agencyMaxHours,
            minStartDate,
            maxStartDate,
            maxTimeBeforeStart,
            locale: frLocale,
        });
    }

    useEffect(() => {
        let day = null
        if (values.start_date) {
            const stDate = isValid(values.start_date) ? values.start_date : parseISO(values.start_date)
            day = getDay(stDate);
        }
        let hours = []
        let hourAndMinutes = []
        let unavailableDays = []

        if (values?.start_agency && agencies?.length) {
            const agency = agencies.find((a) => a.id === parseInt(values.start_agency, 10))
            const periodsForCalendar = getAgencyPeriodsForCalendar(agency, bookingParameters)
            unavailableDays = getAllDayUnavailableWeekdays(periodsForCalendar)

            if (day != null) {
                const availabilityPeriods = periodsForCalendar?.filter((item) => item?.day === day)
                const operatingHours = agency?.operating_hours?.filter((item) => item?.day === day && item.type === 'booking_start')

                if (operatingHours?.length > 0) {
                    operatingHours.forEach((oh) => {
                        hours = [...hours, ...sandbox.range(Number(oh.min_hour?.split(':')[0]), Number(oh.max_hour?.split(':')[0]) + 1)]
                        if (['30'].includes(oh?.min_hour?.split(':')[1])) {
                            hourAndMinutes = [...hourAndMinutes, { time: oh?.min_hour, type: 'start' }]
                        }
                        if (['00', '30'].includes(oh?.max_hour?.split(':')[1])) {
                            hourAndMinutes = [...hourAndMinutes, { time: oh?.max_hour, type: 'end' }]
                        }
                    })
                } else if (availabilityPeriods?.length > 0) {
                    availabilityPeriods.forEach((period) => {
                        if (period.status === 'available') {
                            hours = [...hours, ...sandbox.range(Number(period?.start_hour?.split(':')[0]), Number(period?.end_hour?.split(':')[0]) + 1)]
                            if (['30'].includes(period?.start_hour?.split(':')[1])) {
                                hourAndMinutes = [...hourAndMinutes, { time: period?.start_hour, type: 'start' }]
                            }
                            if (['00', '30'].includes(period?.end_hour?.split(':')[1])) {
                                hourAndMinutes = [...hourAndMinutes, { time: period?.end_hour, type: 'end' }]
                            }
                        }
                    })
                }
            }

            if (day != null && values.start_date && agency?.closing_periods?.length) {
                const stDate = isValid(values.start_date) ? values.start_date : parseISO(values.start_date)
                hours = hours.filter((h) => !isHourBlockedByClosing(stDate, h, agency.closing_periods))
            }
        }

        hours?.sort((a, b) => a - b)

        setStartAgencyHours(hours)
        setStartHoursAndMinutes(hourAndMinutes)
        setStartUnavailableDays(unavailableDays)
    }, [values.start_date, values?.start_agency, agencies]);

    useEffect(() => {
        if (!search.start_day || !startAgencyHours?.length) return;

        const currentHour = Number(search.start_hour?.split(':')?.[0]);
        if (Number.isNaN(currentHour)) return;

        const minHour = startAgencyHours[0];
        const maxHour = startAgencyHours[startAgencyHours.length - 1];
        if (currentHour >= minHour && currentHour <= maxHour) return;

        const hourValue = currentHour < minHour
            ? `${sandbox.numberPad(minHour, 2)}:00`
            : `${sandbox.numberPad(maxHour, 2)}:00`;

        const updated = { ...search, start_hour: hourValue };
        if (shouldUpdateEndHour(updated.end_day, hourValue)) {
            updated.end_hour = hourValue;
        }
        const res = formatResult(updated);
        if (!isValid(res.start_date)) return;

        const resHour = Number(format(res.start_date, 'H'));
        if (resHour < minHour || resHour > maxHour) return;

        if (isValid(values.start_date)) {
            const valuesHour = format(values.start_date, 'HH:mm');
            const resTime = format(res.start_date, 'HH:mm');
            if (valuesHour === resTime) return;
        }

        onChange?.({ ...res });
    }, [startAgencyHours, search.start_day, search.start_hour, values.start_date]);


    useEffect(() => {
        let hours = []
        let hourAndMinutes = []
        let unavailableDays = []

        if (values?.end_agency && agencies?.length) {
            const agency = agencies.find((a) => a.id === parseInt(values.end_agency, 10))
            const periodsForCalendar = getAgencyPeriodsForCalendar(agency, bookingParameters)
            unavailableDays = getAllDayUnavailableWeekdays(periodsForCalendar)

            if (values.end_date) {
                const endDate = isValid(values.end_date) ? values.end_date : parseISO(values.end_date)
                const day = getDay(endDate)

                const availabilityPeriods = periodsForCalendar?.filter((item) => item?.day === day)
                const operatingHours = agency?.operating_hours?.filter((item) => item?.day === day && item.type === 'booking_end')

                if (operatingHours?.length > 0) {
                    operatingHours.forEach((oh) => {
                        hours = [...hours, ...sandbox.range(Number(oh.min_hour?.split(':')[0]), Number(oh.max_hour?.split(':')[0]) + 1)]
                        if (['30'].includes(oh?.min_hour?.split(':')[1])) {
                            hourAndMinutes = [...hourAndMinutes, { time: oh?.min_hour, type: 'start' }]
                        }
                        if (['00', '30'].includes(oh?.max_hour?.split(':')[1])) {
                            hourAndMinutes = [...hourAndMinutes, { time: oh?.max_hour, type: 'end' }]
                        }
                    })
                } else if (availabilityPeriods?.length > 0) {
                    availabilityPeriods.forEach((period) => {
                        if (period.status === 'available') {
                            hours = [...hours, ...sandbox.range(Number(period?.start_hour?.split(':')[0]), Number(period?.end_hour?.split(':')[0]) + 1)]
                            if (['30'].includes(period?.start_hour?.split(':')[1])) {
                                hourAndMinutes = [...hourAndMinutes, { time: period?.start_hour, type: 'start' }]
                            }
                            if (['00', '30'].includes(period?.end_hour?.split(':')[1])) {
                                hourAndMinutes = [...hourAndMinutes, { time: period?.end_hour, type: 'end' }]
                            }
                        }
                    })
                }
            }

            if (values.end_date && agency?.closing_periods?.length) {
                const endDate = isValid(values.end_date) ? values.end_date : parseISO(values.end_date)
                hours = hours.filter((h) => !isHourBlockedByClosing(endDate, h, agency.closing_periods))
            }
        }

        hours?.sort((a, b) => a - b)

        setEndAgencyHours(hours)
        setEndHoursAndMinutes(hourAndMinutes)
        setEndUnavailableDays(unavailableDays)
    }, [values.end_date, values?.end_agency, agencies]);

    const [inputModeValues, setInputModeValues] = useState()
    const [durationError, setDurationError] = useState([])

    const isAgencyDisplayMode = useMemo(() => {
        return [searchModalDisplayMode.start_agency, searchModalDisplayMode.end_agency].includes(displayMode)
    }, [displayMode]);

    useEffect(() => {
        setSearch(prevState => {
            const newState = {...prevState}
            if(isValid(values?.start_date)) {
                newState.start_day = format(values?.start_date, "yyyy-MM-dd")
                newState.start_hour = format(values?.start_date, "HH:mm")
            } else if(values?.start_date !== '') {
                newState.start_day = values?.start_date?.split(' ')[0] ?? ''
                newState.start_hour = values?.start_date?.split(' ')[1] ?? ''
            }
            if(isValid(values?.end_date)) {
                newState.end_day = format(values?.end_date, "yyyy-MM-dd")
                newState.end_hour = format(values?.end_date, "HH:mm")
            } else if(values?.end_date != null && values?.end_date !== '') {
                newState.end_day = values?.end_date?.split(' ')[0] ?? ''
                newState.end_hour = values?.end_date?.split(' ')[1] ?? ''
            } else {
                newState.end_day = ''
                newState.end_hour = ''
            }
            if(values?.start_agency) {newState.start_agency = values.start_agency}
            if(values?.end_agency) {newState.end_agency = values.end_agency}
            return newState
        })
    }, [values])
    useEffect(() => {
        if(search.start_agency == null) {
            const defaultAgency = agencies?.find((item) => item.enable_on_website && item.is_default)
            if(defaultAgency) {
                setSearch(prevState => {
                    const vals = {...prevState, start_agency: defaultAgency.id, end_agency: defaultAgency.id}
                    return vals
                })
            }
        }
    }, [agencies])
    useEffect(() => {
        if(values) {
            setSearch(prevState => {
                let newState = {...prevState}
                if(isValid(values?.start_date)) {
                    newState = {
                        ...newState,
                        start_day: format(values?.start_date, 'yyyy-MM-dd') ?? '',
                        start_hour: format(values?.start_date, 'HH:mm') ?? '',
                    }
                }
                if(isValid(values?.end_date)) {
                    newState = {
                        ...newState,
                        end_day: format(values?.end_date, 'yyyy-MM-dd') ?? '',
                        end_hour: format(values?.end_date, 'HH:mm') ?? '',
                    }
                }
                return newState
            })
        }
    }, [selectedInput])
    useEffect(() => {
        if(selectedInput) {
            setModalOpen(true)
            setDisplayMode(selectedInput)
        }
    }, [toggleOpen,selectedInput]);
    useEffect(() => {
        const result = formatResult()
        setInputModeValues(prevState => {
            const newState = {...prevState}
            if(result?.end_agency !== '') {
                newState.start_agency = sandbox.getAgencyName(agencies,result.start_agency)
            }
            if(result?.end_agency !== '') {
                newState.end_agency = sandbox.getAgencyName(agencies,result.end_agency)
            }
            if(result.start_date) {
                newState.start_day = format(result.start_date, 'dd/MM/yyyy')
                newState.start_hour = format(result.start_date, 'HH:mm')
            }
            if(result.end_date) {
                newState.end_day = format(result.end_date, 'dd/MM/yyyy')
                newState.end_hour = format(result.end_date, 'HH:mm')
            }
            return newState
        })
    }, [search])

    const shouldUpdateEndHour = (day, startHour) => {
        const endDate = parseISO(day)
        let isValidHour = true
        if(isValid(endDate)) {
            const dayOfWeek = getDay(endDate)
            const operatingHours = endAgency?.operating_hours?.filter(item => item.day === dayOfWeek && item.type === 'booking_end')
            if(operatingHours?.length) {
                isValidHour = operatingHours.some(item => {
                    return startHour >= item.min_hour && startHour <= item.max_hour
                })
            }
        }
        return isValidHour
    }

    // Détermine la date/heure de retour proposée par défaut, en tenant compte du minimum
    // ET du maximum de location de l'agence (jours puis heures), pour éviter de proposer
    // par défaut un retour hors des bornes autorisées (ex: min 3h / max 12h ne doit pas
    // proposer +24h, qui ne laisserait aucun véhicule disponible).
    const computeAutoReturnDateTime = (startDayStr, startHourStr) => {
        if (!startDayStr) { return null }

        const startDateTime = parse(`${startDayStr} ${startHourStr || '00:00'}`, 'yyyy-MM-dd HH:mm', new Date())
        if (!isValid(startDateTime)) { return null }

        let hoursToAdd
        if (agencyMinDays > 0) {
            // Minimum exprimé en jours : on garde la logique initiale (même heure, +N jours).
            hoursToAdd = agencyMinDays * 24
        } else if (agencyMinHours <= 0) {
            // Aucun minimum configuré (ni jours, ni heures) : comportement par défaut +24h.
            hoursToAdd = 24
        } else {
            // Un minimum en heures est configuré : il faut aussi respecter le maximum.
            const effectiveMaxHours = agencyMaxDays > 0 ? (agencyMaxDays * 24) : (agencyMaxHours || 0)
            if (effectiveMaxHours > 0 && effectiveMaxHours <= 24) {
                // Le maximum est atteint avant 24h : proposer directement la borne maximale.
                hoursToAdd = effectiveMaxHours
            } else {
                // Pas de maximum, ou maximum au-delà de 24h : +24h reste dans la fenêtre autorisée.
                hoursToAdd = 24
            }
        }

        const endDateTime = addHours(startDateTime, hoursToAdd)
        return {
            end_day: format(endDateTime, 'yyyy-MM-dd'),
            // Tant que l'heure de départ n'est pas encore choisie, on ne fixe pas d'heure de retour.
            end_hour: startHourStr ? format(endDateTime, 'HH:mm') : '',
        }
    }

    const handleChange = ({name, value}, goToNextStep = true) => {
        const errors = []
        let updated = {...search}
        setSearch(prevState => {
            let newState = {...prevState}
            if(name === "start_agency" ) {
                newState = {...prevState, end_agency: value}
            } else if (name === "start_day") {
                const calDayDiff = differenceInDays(parseISO(newState.end_day), parseISO(value))
                if (value >= newState.end_day || (agencyMinDays > 1 && calDayDiff < agencyMinDays - 1)) {
                    const auto = computeAutoReturnDateTime(value, newState.start_hour)
                    if (auto) {
                        newState.end_day = auto.end_day
                    }
                }
            }
            if(name === 'start_hour') {
                const auto = computeAutoReturnDateTime(newState.start_day, value)
                if (auto && shouldUpdateEndHour(auto.end_day, auto.end_hour)) {
                    newState.end_day = auto.end_day
                    newState.end_hour = auto.end_hour
                } else if(shouldUpdateEndHour(newState.end_day, value)) {
                    newState.end_hour = value
                }
            }
            updated = {...newState, [name] : value}
            return updated
        })
        if(name === "start_agency" ) {
            updated = {...updated, end_agency: value}
        } else if (name === "start_day") {
            if(startAgencyHours?.length) {
                const startHour =  Number(updated.start_hour?.split(':')?.[0])

                console.log({startHour, startAgencyHours})
            }

            const calDayDiff = differenceInDays(parseISO(updated.end_day), parseISO(value))
            if (value >= updated.end_day || (agencyMinDays > 1 && calDayDiff < agencyMinDays - 1)) {
                const auto = computeAutoReturnDateTime(value, updated.start_hour)
                if (auto) {
                    updated.end_day = auto.end_day
                }
            }
        }
        if(name === 'start_hour') {
            const auto = computeAutoReturnDateTime(updated.start_day, value)
            if (auto && shouldUpdateEndHour(auto.end_day, auto.end_hour)) {
                updated.end_day = auto.end_day
                updated.end_hour = auto.end_hour
            } else if(shouldUpdateEndHour(updated.end_day, value)) {
                updated.end_hour = value
            }
        }
        if (name === 'end_day' ) {
            if (!shouldUpdateEndHour(value, updated.end_hour)) {
                updated.end_hour = updateEndHour(value, updated.end_hour, endAgency);
            }
        }
        if (name === 'end_hour') {
            if (shouldUpdateEndHour(updated.end_day, value)) {
                updated.end_hour = updateEndHour(updated.end_day, value, endAgency);
            }
        }
        updated = {...updated, [name] : value}
        const res = formatResult(updated)


        if(isValid(res.start_date) && isValid(res.end_date)) {
            errors.push(...getValidationErrorsForResult(res))
            const formValid = errors.length === 0


            if(formValid) {
                onChange && onChange({...res})
                goToNextStep && showNextDisplayMode(res)
            }
        }
        else {
            onChange && onChange({...res})
            goToNextStep && showNextDisplayMode(res)
        }
        setDurationError(errors)
        setInputModeValues(updated)
    }

    const updateEndHour = (value, endHour, endAgency) => {
        const dayOfWeek = getDay(parseISO(value));
        const operatingHours = endAgency?.operating_hours?.filter(item => item.day === dayOfWeek && item.type === 'booking_end');
        if (operatingHours?.length) {
            let isValidHour = operatingHours[0]?.min_hour ?? null;
            if (isValidHour){
                return isValidHour
            }
        }
        return endHour;
    }
    const handleModalClose = () => {
        setModalOpen(false)
    }
    const showNextDisplayMode = (formattedResult) => {
        // const keyList = Object.keys(searchModalDisplayMode);
        if(dateStepOrder.includes(displayMode)) {
            const currentStep = dateStepOrder.indexOf(displayMode)
            if(currentStep != null && ((currentStep+1) < dateStepOrder.length) ) {
                const nextKey = dateStepOrder[currentStep + 1]
                setDisplayMode(searchModalDisplayMode[nextKey])
            } else {
                setModalOpen(false)
                onSearch && onSearch(formattedResult ?? formatResult())
            }
        } else {
            setModalOpen(false)
            onSearch && onSearch(formattedResult ?? formatResult())
        }
    }
    const handleBack = () => {
        const keyList = Object.keys(searchModalDisplayMode);
        const currentStep = keyList.indexOf(displayMode)
        if(currentStep != null && ((currentStep-1) >= 0) ) {
            const nextKey = keyList[currentStep - 1]
            setDisplayMode(searchModalDisplayMode[nextKey])
        } else {
            setModalOpen(false)
        }
    }
    const formatResult = (updated) => {
        const data = updated ?? search
        let startDate = null
        let endDate = null
        if( data.start_day !== '') {
            let startDateStr
            let hourValue = null
            if(data.start_hour !== '') {
                hourValue = data.start_hour
            }
            // Quand les horaires de depart/retour personnalises sont actifs, ils REMPLACENT
            // les horaires d'ouverture : on ne doit pas enchainer les deux bornages, sinon
            // availability_periods rabat deja l'heure avant qu'operating_hours soit consulte.
            const startOperatingHours = startAgency?.allow_operating_hour
                ? startAgency?.operating_hours?.filter(item => item.type === 'booking_start') ?? []
                : []
            if(startOperatingHours.length > 0) {
                hourValue = getAgencyAvailabilityLimit(startOperatingHours, hourValue, data.start_day)
            } else {
                hourValue = getAgencyAvailabilityLimit(startAgency?.availability_periods, hourValue, data.start_day)
            }
            if(hourValue === null) {
                startDate = parse(`${data.start_day}`, 'yyyy-MM-dd', new Date())
            } else {
                startDateStr = `${data.start_day} ${hourValue}`
                startDate = parse(startDateStr, 'yyyy-MM-dd HH:mm', new Date())
            }
            startDate = getFirstOpenDateAfterClosing(startDate, startAgency?.closing_periods)
        }
        if(data.end_day !== '') {
            let endDateStr
            let hourValue = null
            if(data.end_hour !== '') {
                hourValue = data.end_hour
            }
            // Idem pour le retour : les horaires personnalises remplacent les horaires d'ouverture.
            const endOperatingHours = endAgency?.allow_operating_hour
                ? endAgency?.operating_hours?.filter(item => item.type === 'booking_end') ?? []
                : []
            if(endOperatingHours.length > 0) {
                hourValue = getAgencyAvailabilityLimit(endOperatingHours, hourValue, data.end_day)
            } else {
                hourValue = getAgencyAvailabilityLimit(endAgency?.availability_periods, hourValue, data.end_day)
            }

            if(hourValue === null) {
                endDate = parse(`${data.end_day}`, 'yyyy-MM-dd', new Date())
            } else {
                endDateStr = `${data.end_day} ${hourValue}`
                endDate = parse(endDateStr, 'yyyy-MM-dd HH:mm', new Date())
            }
            endDate = getFirstOpenDateAfterClosing(endDate, endAgency?.closing_periods)
        }
         const result = {
            start_agency: data.start_agency,
            end_agency: data.end_agency,
            start_date: isValid(startDate) ? startDate : null,
            end_date: isValid(endDate) ? endDate : null,
        }
        // console.log(result)
        return result
    }

    const getAgencyAvailabilityLimit = (availabilityPeriods = [], hour, date) => {
        const periods = availabilityPeriods.filter(period => {
            const day = getDay(parseISO(date))
            return day === period.day
        })
        if(periods?.length === 0 && hour === null) { return "12:00" }

        if(hour?.length === 4) { hour = `0${hour}` }

        let minHour = null
        let maxHour = null

        let isInPeriod = false;
        let periodsBounds = []

        periods?.forEach(period => {

            const periodStart = period.start_hour ?? period.min_hour
            const periodEnd = period.end_hour ?? period.max_hour

            periodsBounds?.push(periodStart)
            periodsBounds?.push(periodEnd)

            if (minHour === null || periodStart < minHour) {
                minHour = periodStart
            }
            if (maxHour === null || periodEnd > maxHour) {
                maxHour = periodEnd
            }

            if(periodStart <= hour && periodEnd >= hour) {
                isInPeriod = true;
            }
        })
        if(hour === null) { return  minHour }
        if(minHour != null && minHour >= hour){ return minHour }
        if(maxHour != null && maxHour <= hour){ return maxHour }

        if(!isInPeriod) {
            // find the closest hour in the period bounds
            const closestHour = periodsBounds?.find(period => {
                return period >= hour
            })
            if(closestHour) {
                hour = closestHour
            }
        }
        return hour
    }

    const handleSearchButtonClick = () => {
        const result = formatResult()
        const errors = getValidationErrorsForResult(result, { requireDateStep: false })

        if (errors.length > 0) {
            setDurationError(errors)
            return
        }

        setDurationError([])
        setModalOpen(false)
        onSearch && onSearch(result)
    }

    const blockingDurationErrors = useMemo(() => {
        const result = formatResult()
        return getValidationErrorsForResult(result, { requireDateStep: false })
    }, [search, agencyMinDays, agencyMinHours, agencyMaxDays, agencyMaxHours, minStartDate, maxStartDate, maxTimeBeforeStart, displayMode])

    const hasDurationError = durationError.length > 0 || blockingDurationErrors.length > 0
    const handleDisplayModeChange = (newMode) => {
        setDisplayMode(newMode)
    }

    return (
        <Modal
            dialogClassName={classes.modal}
            show={modalOpen}
            fullscreen={"xl-down"}
            backdropClassName={'bg-transparent'}
            contentClassName={"w-100"}
            onHide={() => setModalOpen(false)}
            centered
            animation={true}
        >
            <ModalHeader className={cx(classes['modal-title'], "d-flex flex-column px-2")}>
                <div className={'d-flex justify-content-between w-100 align-items-center'}>
                    <p className={"medium fs18"}>{title[displayMode]}</p>
                    <Button onClick={handleModalClose}>Fermer</Button>
                </div>
                {dateStepOrder.includes(displayMode) ? <>
                    <div className={"d-flex flex-column w-100 mt-1 border-top pt-1"}>
                        <div className={"d-flex justify-content-between w-100 mb-1"}>
                            <div className={"d-flex w-100 pe-1"}>
                                <div className={"w-100"}>
                                    <input
                                        className={cx("form-control px-1 text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.start_day})}
                                        placeholder={"date"}
                                        readOnly
                                        onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_day)}
                                        value={inputModeValues?.start_day ?? ""}
                                    />
                                    {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_day)} className={"w-100 mx-1"}>Start date</Button>*/}
                                </div>
                                <div className={"ps-1 w-75"}>
                                    <input
                                        className={cx("form-control text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.start_hour})}
                                        placeholder={"heure"}
                                        onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_hour)}
                                        readOnly
                                        value={inputModeValues?.start_hour ?? ""}
                                    />
                                    {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_day)} className={"w-100"}>End date</Button>*/}
                                </div>
                            </div>
                            <div className={"d-flex w-100"}>
                                <div className={"pe-1 w-100"}>
                                    <input
                                        className={cx("form-control px-1 text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.end_day})}
                                        placeholder={"date"}
                                        onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_day)}
                                        readOnly
                                        value={inputModeValues?.end_day ?? ""}
                                    />
                                    {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_date)} className={"w-100 mx-1"}>Start hour</Button>*/}
                                </div>
                                <div className={"ps-1 w-75"}>
                                    <input
                                        className={cx("form-control text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.end_hour})}
                                        placeholder={"heure"}
                                        onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_hour)}
                                        readOnly
                                        value={inputModeValues?.end_hour ?? ""}
                                    />
                                    {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_hour)} className={"w-100"}>End hour</Button>*/}
                                </div>
                            </div>
                        </div>
                    </div>
                </> : <></>}
            </ModalHeader>
            <ModalBody>
                {durationError?.length > 0 && (
                    <div className={"bg-danger-light p-3 text-center c-danger mb-2"}>
                        {durationError.map((item,index) => {
                            return <p className={"medium"} key={`duration-error-item-${index}`}>{item}</p>
                        })}
                    </div>
                )}
                {displayMode === searchModalDisplayMode.recap && (
                    <>
                        <div className={"d-flex flex-column w-100"}>
                            {agencies?.length > 1 && !disableAgencySelect && (
                                <div className={"w-100 pe-1"}>
                                    <p>Agence{!singleAgency && " de départ"}</p>
                                    <input
                                        className={cx("form-control text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.start_agency})}
                                        readOnly
                                        placeholder={"agence de départ"}
                                        onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_agency)}
                                        value={inputModeValues?.start_agency ?? ""}
                                    />
                                    {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_agency)} className={"w-100 mx-1"}>Start agency</Button>*/}
                                </div>
                            )}
                            <div className={"w-100 my-3"}>
                                <p>Date de départ</p>
                                <div className={"d-flex w-100"}>
                                    <div className={"w-100"}>
                                        <input
                                            className={cx("form-control px-1 text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.start_day})}
                                            placeholder={"date"}
                                            id={'startDate'}
                                            readOnly
                                            onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_day)}
                                            value={inputModeValues?.start_day ?? ""}
                                        />
                                        {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_day)} className={"w-100 mx-1"}>Start date</Button>*/}
                                    </div>
                                    <div className={"ps-1 w-75"}>
                                        <input
                                            className={cx("form-control text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.start_hour})}
                                            placeholder={"heure"}
                                            onClick={() => handleDisplayModeChange(searchModalDisplayMode.start_hour)}
                                            readOnly
                                            value={inputModeValues?.start_hour ?? ""}
                                        />
                                        {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_day)} className={"w-100"}>End date</Button>*/}
                                    </div>
                                </div>
                            </div>
                            {(agencies?.length > 1 && !singleAgency && !disableAgencySelect) && (
                                <div className={"w-100"}>
                                    <p>Agence de retour</p>
                                    <input
                                        className={cx("form-control text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.end_agency})}
                                        onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_agency)}
                                        placeholder={"agence de départ"}
                                        value={inputModeValues?.end_agency ?? ""}
                                        readOnly
                                    />
                                    {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_agency)} className={"w-100"}>End agency</Button>*/}
                                </div>
                            )}
                            <div className={"w-100 my-3"}>
                                <p>Date de retour</p>
                                <div className={"d-flex w-100"}>
                                    <div className={"pe-1 w-100"}>
                                        <input
                                            className={cx("form-control px-1 text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.end_day})}
                                            placeholder={"date"}
                                            id={'endDate'}
                                            onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_day)}
                                            readOnly
                                            value={inputModeValues?.end_day ?? ""}
                                        />
                                        {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_date)} className={"w-100 mx-1"}>Start hour</Button>*/}
                                    </div>
                                    <div className={" w-75"}>
                                        <input
                                            className={cx("form-control text-center no-border shadow-none pointer", {'bg-main-light': displayMode === searchModalDisplayMode.end_hour})}
                                            placeholder={"heure"}
                                            onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_hour)}
                                            readOnly
                                            value={inputModeValues?.end_hour ?? ""}
                                        />
                                        {/*<Button onClick={() => handleDisplayModeChange(searchModalDisplayMode.end_hour)} className={"w-100"}>End hour</Button>*/}
                                    </div>
                                </div>
                            </div>

                            {warningMessage && <p className={"fs12 medium mt-4 fst-italic"} style={{color: "#ff4f4a"}}>{warningMessage}</p>}
                        </div>
                    </>
                    // <AgencyPicker agencies={agencies} name={"start_agency"} selected={search.start_agency} onChange={handleChange} checkColor={color} />
                )}
                {displayMode === searchModalDisplayMode.start_agency && !disableAgencySelect && (
                    <AgencyPicker agencies={agencies} name={"start_agency"} selected={search.start_agency} onChange={handleChange} checkColor={color} />
                )}
                {displayMode === searchModalDisplayMode.start_day && (
                    <DatePicker
                        mode={'start'}
                        onSelect={handleChange}
                        name={"start_day"}
                        startDateStr={search.start_day}
                        endDateStr={search.end_day}
                        unavailableDays={startUnavailableDays}
                        closingPeriods={startAgency?.closing_periods ?? []}
                    />
                )}
                {displayMode === searchModalDisplayMode.start_hour && (
                    <TimePicker
                        day={search.start_day}
                        mode={'start'}
                        name={'start_hour'}
                        // withLimit={search.start_day !== '' && search.start_day === search.end_day }
                        max={search.end_hour}
                        value={search.start_hour}
                        onChange={handleChange}
                        onBackButtonClick={handleBack}
                        hourDiff={agencyMinHours}
                        agencyHours={startAgencyHours}
                        agency={startAgency}
                        hoursAndMinutes={startHoursAndMinutes}
                        confirmDisabled={hasDurationError}
                    />
                )}
                {displayMode === searchModalDisplayMode.end_agency && !disableAgencySelect && (
                    <AgencyPicker agencies={agencies} name={"end_agency"} selected={search.end_agency} onChange={handleChange} checkColor={color}/>
                )}
                {displayMode === searchModalDisplayMode.end_day && (
                    <DatePicker
                        mode={'end'}
                        onSelect={handleChange}
                        name={"end_day"}
                        startDateStr={search.start_day}
                        endDateStr={search.end_day}
                        unavailableDays={endUnavailableDays}
                        closingPeriods={endAgency?.closing_periods ?? []}
                    />
                )}
                {displayMode === searchModalDisplayMode.end_hour && (
                    <TimePicker
                        day={search.end_day}
                        mode={'end'}
                        name={'end_hour'}
                        withLimit={search.start_day !== '' && search.start_day === search.end_day}
                        min={search.start_hour}
                        value={search.end_hour}
                        onChange={handleChange}
                        hourDiff={agencyMinHours}
                        maxHourDiff={agencyMaxHours}
                        onBackButtonClick={handleBack}
                        agencyHours={endAgencyHours}
                        agency={endAgency}
                        hoursAndMinutes={endHoursAndMinutes}
                        confirmDisabled={hasDurationError}
                    />
                )}
            </ModalBody>
            {onSearch && (
                <div className={"d-xl-none"}>
                    <ModalFooter >
                        <Button
                            className={"w-100"}
                            onClick={handleSearchButtonClick}
                            disabled={hasDurationError}
                            variant={hasDurationError ? "secondary" : "primary"}
                        >
                            Rechercher
                        </Button>
                    </ModalFooter>
                </div>
            )}
        </Modal>
    );
};

export default SearchModal;
